import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePrevious } from '~/shared/lib/use-previous'

export function usePagination({ startPage, endPage }: { startPage: number; endPage?: number }) {
  const [totalDocumentPages, setTotalDocumentPages] = useState(0)
  const scrollableRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [pageText, setPageText] = useState<string>('1')
  const prevPageText = usePrevious(pageText)

  const initPagesOnDocumentLoad = useCallback((numPages: number) => {
    setTotalDocumentPages(numPages)
    // Initialize refs array for all pages
    pageRefs.current = Array(numPages).fill(null)
  }, [])

  const pagesToDisplay = useMemo(
    () =>
      Array.from(
        { length: (endPage ?? totalDocumentPages) - startPage + 1 },
        (_, i) => startPage + i,
      ),
    [endPage, startPage, totalDocumentPages],
  )

  const scrollToPage = useCallback((page: number) => {
    const pageIndex = page - 1
    if (pageIndex < 0 || pageIndex >= pageRefs.current.length) return

    const pageRef = pageRefs.current[pageIndex]
    if (pageRef) {
      pageRef.scrollIntoView({ block: 'start' })
    }
  }, [])

  // Add scroll event listener to update current page based on scrolling
  useEffect(() => {
    if (!scrollableRef.current) return

    const handleScroll = () => {
      if (!scrollableRef.current || pageRefs.current.length === 0) return

      // Find which page is most visible in the viewport
      const containerTop = scrollableRef.current.scrollTop
      const containerHeight = scrollableRef.current.clientHeight
      const containerBottom = containerTop + containerHeight

      let mostVisiblePage = 1
      let maxVisibleArea = 0

      pageRefs.current.forEach((pageRef, index) => {
        if (!pageRef) return

        const rect = pageRef.getBoundingClientRect()
        const pdfRect = scrollableRef.current!.getBoundingClientRect()

        // Calculate position relative to the PDF container
        const pageTop = rect.top - pdfRect.top + scrollableRef.current!.scrollTop
        const pageBottom = pageTop + rect.height

        // Calculate the visible area of this page
        const visibleTop = Math.max(containerTop, pageTop)
        const visibleBottom = Math.min(containerBottom, pageBottom)
        const visibleArea = Math.max(0, visibleBottom - visibleTop)

        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea
          mostVisiblePage = index + 1
        }
      })

      if (mostVisiblePage.toString() !== pageText) {
        setPageText(mostVisiblePage.toString())
      }
    }

    const pdfContainer = scrollableRef.current
    pdfContainer.addEventListener('scroll', handleScroll)

    return () => {
      pdfContainer.removeEventListener('scroll', handleScroll)
    }
  }, [scrollableRef, pageText])

  // Handle page number input change
  const handlePageTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      // User clears the input
      if (value === '') {
        setPageText('')
        return
      }

      // User types a non-numeric character
      const pageNumber = Number(value)
      if (isNaN(pageNumber) || pageNumber < 1) {
        setPageText(prevPageText ?? '1')
        return
      }

      // User types a number greater than total pages
      if (pageNumber > pagesToDisplay.length) {
        setPageText(pagesToDisplay.length.toString())
        scrollToPage(pagesToDisplay.length)
        return
      }

      // User types valid page number
      setPageText(value)
      scrollToPage(pageNumber)
    },
    [pagesToDisplay.length, prevPageText, scrollToPage],
  )

  const assignPageRef = (el: HTMLDivElement | null, index: number) => {
    pageRefs.current[index] = el
  }

  return {
    initPagesOnDocumentLoad,
    scrollableRef,
    pagesToDisplay,
    pageText,
    handlePageTextChange,
    assignPageRef,
  }
}
